import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { Card, CardContent } from '../card/card';

export type DashboardCardColors = {
  background: string;
  title: string;
  value: string;
  description: string;
};

export type DashboardCardProps = {
  title: ReactNode;
  value: ReactNode;
  description: ReactNode;
  colors: DashboardCardColors;
  onClick?: (
    event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>,
  ) => void;
};

export const DashboardCard = ({
  title,
  value,
  description,
  colors,
  onClick,
}: DashboardCardProps): JSX.Element => {
  const isClickable = onClick !== undefined;

  const handleClick = (
    event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>,
  ): void => {
    if (!isClickable) {
      return;
    }

    onClick(event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick(event);
    }
  };

  return (
    <Box
      onClick={isClickable ? handleClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={typeof title === 'string' ? title : 'Dashboard card'}
      sx={{
        borderRadius: '12px',
        ...(isClickable ? { cursor: 'pointer' } : {}),
      }}
    >
      <Card
        variant='none'
        hover={isClickable}
        sx={{ backgroundColor: colors.background, borderRadius: '12px' }}
      >
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant='labelSmall' sx={{ color: colors.title }}>
              {title}
            </Typography>
            <Typography variant='headingLarge' sx={{ color: colors.value }}>
              {value}
            </Typography>
            <Typography variant='labelSmall' sx={{ color: colors.description }}>
              {description}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
